import { UseZodFormReturn, useZodForm } from '@repo/hooks/useZodForm';
import { ZodObjecty, type TypeOfOptional } from '@repo/types/Types';
import React, {
  useContext,
  useEffect,
  useRef,
  createContext,
  FormEventHandler,
  MutableRefObject,
  useMemo,
  useState,
} from 'react';
import { DefaultValues } from 'react-hook-form';
import { TypeOf } from 'zod';
import { Form } from './Form';
import { useApolloClient } from '@apollo/client/react';
import { getZodDefaults } from '@repo/util/getZodDefaults';
import { CRUD } from '@repo/util/CRUD';
import { usePromise } from '@repo/hooks/usePromise';
import { useZodFormContext } from '@repo/hooks/useZodFormContext';

export interface CRUDFormProps<
  Schema extends ZodObjecty,
  Data extends DefaultValues<TypeOf<Schema>>,
> {
  schema: Schema;
  className?: string;
  itemId?: string | undefined;
  onCreate?: (id: string) => void;
  onDelete?: (id: string) => void;
  onReadError?: (error: Error) => void;
  crud: CRUD<Schema, Data>;
  children?:
    | React.ReactNode
    | ((form: UseZodFormReturn<Schema>, data: Data | undefined) => React.ReactNode);
}

const crudCtx = createContext<null | {
  data: undefined | object;
  internal: MutableRefObject<{
    isDelete: boolean;
    refetch: () => Promise<void>;
  }>;
}>(null);

export type DataOf<T extends CRUD<ZodObjecty>> = ReturnType<T['useRead']>['data'];

export function useCRUDData<T extends CRUD<ZodObjecty> = CRUD<ZodObjecty>>(): DataOf<T> {
  const ctx = useContext(crudCtx);
  if (!ctx) {
    throw new Error('Not inside a CRUDForm');
  }
  return ctx.data;
}

export function useCRUDDelete(): FormEventHandler<HTMLButtonElement> | undefined {
  const formCtx = useZodFormContext();
  const ctx = useContext(crudCtx);
  if (!formCtx) return;
  if (!ctx) return;
  const { data, internal } = ctx;
  if (!data) return;
  return async (event) => {
    internal.current.isDelete = true;
    return await formCtx.formState?.onSubmit?.(event);
  };
}

export function useCRUDRefetch(): undefined | (() => Promise<void>) {
  const ctx = useContext(crudCtx);
  if (!ctx) return;
  const { internal } = ctx;
  if (!internal) return;
  return internal.current.refetch;
}

export function CRUDForm<
  Schema extends ZodObjecty,
  Data extends DefaultValues<TypeOf<Schema>> = DefaultValues<TypeOf<Schema>>,
>({ onCreate, onReadError, onDelete, ...props }: CRUDFormProps<Schema, Data>) {
  const { itemId } = props;
  const [actualId, setActualId] = useState<string>();
  const [error, setError] = useState<Error>();
  useEffect(() => {
    setActualId(itemId);
  }, [itemId]);
  const onActualCreate = useMemo(
    () => (id: string) => {
      setActualId(id);
      setError(undefined);
      onCreate?.(id);
    },
    [onCreate]
  );
  const onActualError = useMemo(
    () => (error: Error) => {
      setError(error);
      onReadError?.(error);
    },
    [onReadError]
  );
  const onActualDelete = useMemo(
    () => (id: string) => {
      setError(undefined);
      setActualId(undefined);
      onDelete?.(id);
    },
    [onDelete]
  );
  if (!actualId || error) {
    return <CRUDCreateForm error={error} onCreate={onActualCreate} {...props} />;
  }
  return (
    <CRUDUpdateForm
      onDelete={onActualDelete}
      onReadError={onActualError}
      {...props}
      itemId={actualId}
    />
  );
}

export function CRUDCreateForm<
  Schema extends ZodObjecty,
  Data extends DefaultValues<TypeOf<Schema>> = DefaultValues<TypeOf<Schema>>,
>({
  schema,
  error,
  onCreate,
  crud: { create },
  className,
  children,
}: Omit<CRUDFormProps<Schema, Data>, 'onDelete' | 'itemId' | 'onReadError'> & {
  error: Error | undefined;
}) {
  const client = useApolloClient();
  const form = useZodForm({
    schema,
    async onSubmit(input) {
      onCreate?.(await create(client, input));
    },
  });
  useEffect(() => {
    if (!error) return;
    form.control.setError('root.server', {
      type: 'response-error',
      message:
        error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : 'unknown-error',
    });
  }, [error, form]);
  return (
    <Form {...form} className={className}>
      {typeof children === 'function' ? children(form, undefined) : children}
    </Form>
  );
}

export function CRUDUpdateForm<
  Schema extends ZodObjecty,
  Data extends DefaultValues<TypeOf<Schema>> = DefaultValues<TypeOf<Schema>>,
>({
  itemId,
  schema,
  crud: { update, useRead, delete: del },
  className,
  children,
  onReadError,
  onDelete,
}: Omit<CRUDFormProps<Schema, Data>, 'onCreate'> & { itemId: string }) {
  const { data, loading, error, refetch } = useRead(itemId);
  const [loadCount, setLoadCount] = useState(0);
  const [requestCount, setRequestCount] = useState(1);
  const dataP = usePromise<Data>();
  const client = useApolloClient();
  const internal = useRef({
    isDelete: false,
    refetch: async () => {
      form.control._subjects.state.next({
        isLoading: true,
      });
      await refetch();
      setRequestCount((count) => count + 1);
    },
  });
  const form = useZodForm<Schema>({
    schema,
    defaultValues: async () => (await dataP.promise) as TypeOfOptional<Schema>,
    async onSubmit(input) {
      if (internal.current.isDelete) {
        internal.current.isDelete = false;
        await del(client, itemId);
        onDelete?.(itemId);
        return;
      } else {
        await update(client, itemId, input, await dataP.promise);
      }
      await refetch();
      setRequestCount((count) => count + 1);
    },
  });
  useEffect(() => {
    if (loading) return;
    if (loadCount === requestCount) return;
    setLoadCount((count) => count + 1);
    if (error) {
      onReadError?.(error);
    } else {
      dataP.done(data, undefined);
      const resetValue = data ?? getZodDefaults(schema);
      form.reset(resetValue as DefaultValues<TypeOfOptional<Schema>>, {
        keepSubmitCount: true,
        keepIsSubmitSuccessful: true,
        keepIsSubmitted: true,
      });
      form.control._subjects.state.next({
        isLoading: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadCount === requestCount, loading]);
  const ctx = useMemo(() => ({ data: dataP.data, internal }), [dataP.data, internal]);
  return (
    <crudCtx.Provider value={ctx}>
      <Form {...form} className={className} column={1}>
        {useMemo(
          () => (typeof children === 'function' ? children(form, dataP.data) : children),
          [children, form, dataP.data]
        )}
      </Form>
    </crudCtx.Provider>
  );
}
